const pickupTypes = new Set(["recogida", "recoleccion"]);
const loadUnloadTypes = new Set(["carga", "descarga"]);
const completedStatuses = new Set(["finalizado", "finalizado_parcial"]);
const incidentStatuses = new Set(["incidencia"]);
const monthNames = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

export function createRouteStopItem(source) {
  const stop = mapRouteStopResponse(source);

  return {
    key: stop.stopId || stop.id,
    stop,
    status: stop.status,
    isCompleted: isCompletedStatus(stop.status),
    isIncident: isIncidentStatus(stop.status),
    isClosed: isClosedStatus(stop.status),
    scheduleLabel: formatRouteStopSchedule(
      stop.manualType,
      stop.type,
      stop.combinedTimeWindow,
    ),
    contact: stop.contact.name,
    displayAddress: stop.inputAddress || stop.addressNormalized.displayAddress,
  };
}

export function sortRouteStops(routeStops) {
  return [...routeStops].sort(
    (left, right) => left.stop.order - right.stop.order,
  );
}

export function buildSummaryChips(routeStops, date = new Date()) {
  return [
    formatDateChip(date),
    formatStopCount(routeStops.length),
  ].filter(Boolean);
}

export function buildPackageChips(routeStop, date = new Date()) {
  return [
    formatDateChip(date),
    formatPackageCount(routeStop?.stop.totalTasks ?? 0),
  ].filter(Boolean);
}

export function completedRouteStopCount(routeStops) {
  return routeStops.filter((item) => item.isClosed).length;
}

export function formatRouteStopSchedule(manualType, routeStopType, schedule) {
  const label = resolveStopTypeLabel(manualType, routeStopType);
  const normalizedSchedule = schedule?.trim() ?? "";

  return normalizedSchedule ? `${label} ${normalizedSchedule}` : label;
}

export function resolveStopTypeLabel(manualType, routeStopType) {
  const normalizedManualType = normalize(manualType);

  if (normalizedManualType === "carga") return "Carga";
  if (normalizedManualType === "descarga") return "Descarga";

  return isPickupType(routeStopType) ? "Recogida" : "Entrega";
}

export function isLoadUnloadManualType(manualType) {
  return loadUnloadTypes.has(normalize(manualType));
}

export function formatPackageCount(packageCount) {
  const normalizedCount = Math.max(0, packageCount);
  const noun = normalizedCount === 1 ? "Paquete" : "Paquetes";
  return `${normalizedCount} ${noun}`;
}

export function isClosedStatus(status) {
  return isCompletedStatus(status) || isIncidentStatus(status);
}

function mapRouteStopResponse(source) {
  const address = source.address_normalized ?? source.addressNormalized ?? {};
  const contact = source.contact ?? {};

  return {
    id: readString(source, "_id", "id"),
    planId: readString(source, "plan_id", "planId"),
    stopId: readString(source, "stop_id", "stopId"),
    contact: {
      name: readString(contact, "name"),
      vatNumber: readString(contact, "vat_number", "vatNumber"),
      email: readString(contact, "email"),
      attentionOf: readString(contact, "attention_of", "attentionOf"),
      phone: readString(contact, "phone"),
    },
    type: readString(source, "type"),
    manualType: readString(source, "manual_type", "manualType"),
    isLoadUnload: isLoadUnloadManualType(
      readString(source, "manual_type", "manualType"),
    ),
    number: readString(source, "number"),
    order: readNumber(source, "order"),
    timeWindow1: readString(source, "time_window_1", "timeWindow1"),
    timeWindow2: readString(source, "time_window_2", "timeWindow2"),
    inputAddress:
      readString(source, "input_address", "inputAddress") ||
      readString(address, "input_address", "inputAddress"),
    addressNormalized: mapAddress(address),
    status: readString(source, "status"),
    statusDescription: readString(
      source,
      "status_description",
      "statusDescription",
    ),
    totalTasks: readNullableNumber(source, "total_tasks", "totalTasks") ?? 1,
    km: readNumber(source, "km"),
    totalTime: readNumber(source, "total_time", "totalTime"),
    get combinedTimeWindow() {
      return joinWithSeparator(" | ", this.timeWindow1, this.timeWindow2);
    },
  };
}

function mapAddress(source) {
  const normalizedAddress = readString(
    source,
    "address",
    "normalized_address",
    "normalizedAddress",
  );
  const label = readString(source, "label");
  const displayAddress = normalizedAddress.trim();

  return {
    normalizedAddress,
    label,
    displayAddress,
    fullAddress: joinWithSeparator("\n", displayAddress, label),
  };
}

function isPickupType(routeStopType) {
  return pickupTypes.has(normalize(routeStopType));
}

function isCompletedStatus(status) {
  return completedStatuses.has(normalize(status));
}

function isIncidentStatus(status) {
  return incidentStatuses.has(normalize(status));
}

function formatDateChip(date) {
  const day = String(date.getDate()).padStart(2, "0");
  return `${day} ${monthNames[date.getMonth()]}`;
}

function formatStopCount(stopCount) {
  const normalizedCount = Math.max(0, stopCount);
  const noun = normalizedCount === 1 ? "Parada" : "Paradas";
  return `${normalizedCount} ${noun}`;
}

function joinWithSeparator(separator, ...values) {
  return values
    .map((value) => value?.trim?.() ?? "")
    .filter(Boolean)
    .join(separator);
}

function readString(source, ...keys) {
  const value = readValue(source, keys);
  return typeof value === "string" ? value.trim() : "";
}

function readNumber(source, ...keys) {
  const value = Number(readValue(source, keys));
  return Number.isFinite(value) ? value : 0;
}

function readNullableNumber(source, ...keys) {
  const value = readValue(source, keys);

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function readValue(source, keys) {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }
  }

  return undefined;
}

function normalize(value) {
  return value?.trim?.().toLowerCase() ?? "";
}
